/**
 * Types for in-person sessions.
 */
import type { UserWithProfile } from '@/types/user.types';
import type { CreateActivityDto } from '@/types/activity.types';
import type { CreateActivityTypeDto } from '@/types/activity-type.types';
import type { AttendanceStatus } from '@/types/attendance.types';

/** In-person + shared session type enum (homework help, tutoring, meetings, …). */
export type SessionType =
  | 'homework_help'
  | 'individual_tutoring'
  | 'workshop'
  | 'whole_class'
  | 'group_tutoring'
  | 'quick_help_desk'
  | 'information_meeting'
  | 'information_meeting_in_person'
  | 'welcome_meeting';

/** Planning mode chosen before creating a session. */
export type PlanificationType = 'individual' | 'collective' | 'information_meeting';

/** In-person sessions list tab. */
export type SessionTab = 'tutoring' | 'information-meeting';

/** Delivery format (`in_person` / `remote` / `hybrid`). */
export type SessionFormat = 'in_person' | 'remote' | 'hybrid';
/** Single occurrence vs recurring series. */
export type SessionMode = 'single' | 'recurring';
/** Session lifecycle including absence outcomes (`BOTH_ABSENT`, `NOT_COMPLETED`, `PARTICIPANTS_ABSENT`, …). */
export type SessionStatus =
  | 'PLANNED'
  | 'TO_LAUNCH'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'CLOSED'
  | 'CANCELLED'
  | 'STUDENT_ABSENT'
  | 'TUTOR_ABSENT'
  | 'BOTH_ABSENT'
  | 'NOT_COMPLETED'
  | 'PARTICIPANTS_ABSENT';
/** Recurrence pattern stored on the session (`weekly`, `ab`, `custom`, …). */
export type RecurrenceType =
  | 'none'
  | 'once'
  | 'daily'
  | 'weekly'
  | 'several_days_per_week'
  | 'biweekly'
  | 'monthly'
  | 'ab'
  | 'custom';

/** Distinguishes tutoring vs information-meeting create/edit flows. */
export type SessionFormKind = 'tutoring_session' | 'information_meeting';

/** Date + start/end used for availability and form time fields. */
export type SessionSlot = {
  sessionDate: string;
  startTime: string;
  endTime: string;
};

/** Editable in-person session fields shared by create and edit. */
export type SessionFormData = {
  establishmentId: string;
  schoolYearId: string;
  roomId: string;
  programId: string;
  levelIds: string[];
  subjectIds: string[];
  classId: string;
  specialtyIds: string[];
  activityId: string;
  sessionDate: string;
  startTime: string;
  endTime: string;
  type: SessionType | '';
  sessionFormat: SessionFormat | '';
  mode: SessionMode | '';
  recurrenceType: RecurrenceType;
  recurrenceRule: Record<string, unknown> | null;
  topic: string;
  joinLink: string;
  planDuringHolidays: boolean;
  internalNotes: string;
};

/** Field-state helpers for the in-person session form. */
export type SessionFormFieldValidation = {
  getFieldState: (fieldName: string) => 'default' | 'error' | 'success';
  getFieldError: (fieldName: string) => string | undefined;
};

/** Setter for one in-person session form field. */
export type SessionFormFieldChange = (
  field: keyof SessionFormData,
  value: string | string[] | boolean | Record<string, unknown> | null,
) => void;

/** PATCH `/sessions/:id` when editing a recurring series (backend enum). */
export type RecurrenceUpdateScope = 'occurrence' | 'following';

/** One field-event budget line (`session.budget.posts`). */
export interface BudgetPost {
  type: string;
  amount: number;
  comment?: string;
}

/** In-person session row (`GET /sessions`). */
export interface Session {
  id: string;
  companyId: string;
  establishmentId: string;
  roomId?: string | null;
  programId?: string | null;
  schoolYearId: string;
  levelIds?: string[];
  levelNames?: string[];
  subjectIds?: string[];
  subjectNames?: string[];
  classId?: string | null;
  specialtyIds?: string[] | null;
  sessionDate: Date;
  startTime: string;
  endTime: string;
  type: SessionType;
  sessionFormat: SessionFormat;
  joinLink?: string | null;
  mode: SessionMode;
  recurrenceType: RecurrenceType;
  recurrenceRule?: Record<string, unknown> | null;
  maxStudents?: number | null;
  maxVolunteers?: number | null;
  activityId?: string | null;
  establishmentName?: string | null;
  programName?: string | null;
  activityName?: string | null;
  roomName?: string | null;
  topic?: string | null;
  durationMinutes?: number | null;
  /** If true (default), scheduling on company holiday/vacation dates is allowed. */
  planDuringHolidays?: boolean;
  internalNotes?: string | null;
  /** Present when this row was created as part of a recurring series. */
  recurrenceGroupId?: string | null;
  /** FK to the {@link SessionGroup} (parent session) this child session belongs to. Null for single creates. */
  sessionGroupId?: string | null;
  status: SessionStatus;
  managedById?: string | null;
  managedBy?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  eventType?: string | null;
  location?: string | null;
  budget?: {
    posts?: BudgetPost[];
    etp?: number;
    unitCost?: number;
    total?: number;
    grandTotal?: number;
  } | null;
  establishment?: {
    id: string;
    name: string;
  };
  activity?: {
    id: string;
    label: string;
    maxTutors?: number | null;
    maxStudents?: number | null;
    maxVolunteers?: number | null;
  };
  createdAt: Date;
  updatedAt: Date;
  _permissions?: Record<string, boolean>;
}

/** Body for `POST /sessions` (in-person). */
export interface CreateSessionDto {
  establishmentId?: string;
  roomId?: string | null;
  programId?: string | null;
  schoolYearId?: string | null;
  levelIds?: string[];
  subjectIds?: string[];
  classId?: string | null;
  specialtyIds?: string[] | null;
  sessionDate: string;
  startTime: string;
  endTime: string;
  type: SessionType;
  sessionFormat?: SessionFormat;
  joinLink?: string | null;
  mode?: SessionMode;
  recurrenceType?: RecurrenceType;
  recurrenceRule?: Record<string, unknown> | null;
  maxStudents?: number | null;
  maxVolunteers?: number | null;
  activityId?: string | null;
  topic?: string | null;
  durationMinutes?: number | null;
  planDuringHolidays?: boolean;
  internalNotes?: string | null;
  tutorIds?: string[];
  studentIds?: string[];
  volunteerIds?: string[];
  animatorIds?: string[];
  /**
   * Optional per-user attendance on create (userId → status). Defaults to present.
   */
  attendanceByUserId?: Record<string, 'present' | 'absent' | 'excused' | 'pending'>;
  /** Explicit tutor–student pairs; if omitted, backend derives studentIds × tutorIds. */
  pairs?: Array<{ studentUserId: string; tutorUserId: string }>;
  newActivity?: CreateActivityDto;
  newActivityType?: CreateActivityTypeDto;
  establishmentName?: string | null;
  programName?: string | null;
  activityName?: string | null;
  roomName?: string | null;
}

/** Body for `PATCH /sessions/:id` (in-person). */
export interface UpdateSessionDto {
  establishmentId?: string | null;
  roomId?: string | null;
  programId?: string | null;
  schoolYearId?: string;
  levelIds?: string[];
  subjectIds?: string[];
  classId?: string | null;
  specialtyIds?: string[] | null;
  sessionDate?: string;
  startTime?: string;
  endTime?: string;
  type?: SessionType;
  sessionFormat?: SessionFormat;
  joinLink?: string | null;
  mode?: SessionMode;
  recurrenceType?: RecurrenceType;
  recurrenceRule?: Record<string, unknown> | null;
  maxStudents?: number | null;
  maxVolunteers?: number | null;
  activityId?: string | null;
  topic?: string | null;
  durationMinutes?: number | null;
  planDuringHolidays?: boolean;
  internalNotes?: string | null;
  tutorIds?: string[];
  studentIds?: string[];
  volunteerIds?: string[];
  animatorIds?: string[];
  managedById?: string | null;
  eventType?: string | null;
  location?: string | null;
  budget?: {
    posts?: BudgetPost[];
    etp?: number;
    unitCost?: number;
    total?: number;
    grandTotal?: number;
  } | null;
  pairs?: Array<{ studentUserId: string; tutorUserId: string }>;
  status?: SessionStatus;
  newActivity?: CreateActivityDto;
  newActivityType?: CreateActivityTypeDto;
  establishmentName?: string | null;
  programName?: string | null;
  activityName?: string | null;
  roomName?: string | null;
  recurrenceUpdateScope?: RecurrenceUpdateScope;
}

/** Participant on an in-person session (role + attendance). */
export interface SessionParticipant {
  id: string;
  sessionId: string;
  userId: string;
  role:
    | 'tutor'
    | 'tutor_remote'
    | 'student'
    | 'student_remote'
    | 'volunteer'
    | 'coordinator_remote'
    | 'recruiter_remote';
  user: UserWithProfile;
  attendanceStatus?: string;
  absenceJustification?: string | null;
  absenceComment?: string | null;
  createdAt: Date;
  updatedAt: Date;
}

/** Session hydrated with participants, facilitators, and pairs. */
export interface SessionWithParticipants extends Session {
  participants?: SessionParticipant[];
  /**
   * Server-computed facilitators/animateurs, only populated for `information_meeting`,
   * `welcome_meeting` and `workshop` (see backend {@link attachFacilitators}). Role is widened
   * since facilitators can carry remote roles (`recruiter_remote`, `coordinator_remote`, ...)
   * not covered by {@link SessionParticipant}['role'].
   */
  facilitators?: Array<Omit<SessionParticipant, 'role'> & { role: string }>;
  studentTutorPairs?: Array<{
    id?: string;
    sessionId?: string;
    studentUserId: string;
    tutorUserId: string;
    assignmentSource?: 'manual' | 'auto_binome_coeur' | 'auto_history' | 'auto_balanced';
    studentUser?: UserWithProfile;
    tutorUser?: UserWithProfile;
  }>;
}

/** Status of a parent {@link SessionGroup}. */
export type SessionGroupStatus = 'PLANNED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';

/** Reason code sent with `POST /sessions/:id/cancel`. */
export type CancelSessionReasonCode =
  | 'STUDENT_ABSENCE'
  | 'TUTOR_ABSENCE'
  | 'VSC_ABSENCE'
  | 'WEATHER'
  | 'ROOM_UNAVAILABLE'
  | 'OTHER';

/** Body for cancelling a session (`dematchMode: 'GLOBAL'` unpairs everyone). */
export interface CancelSessionDto {
  reasonCode: CancelSessionReasonCode;
  reasonDetail?: string;
  dematchMode?: 'GLOBAL';
}

/** Result of cancelling every child in a session group. */
export interface CancelGroupResult {
  cancelledCount: number;
  skippedCount: number;
}

/** Participant on a group child session. */
export interface SessionGroupParticipant {
  id: string;
  sessionId: string;
  userId: string;
  role: 'tutor' | 'student' | 'volunteer';
  attendanceStatus?: string;
  absenceJustification?: string | null;
  user: UserWithProfile;
}

/** Student–tutor pair on a group child session. */
export interface SessionGroupStudentTutorPair {
  id: string;
  sessionId: string;
  studentUserId: string;
  tutorUserId: string;
  assignmentSource: 'manual' | 'auto_binome_coeur' | 'auto_history' | 'auto_balanced';
  studentUser?: UserWithProfile;
  tutorUser?: UserWithProfile;
}

/** Slim report status attached to a group child. */
export interface SessionGroupChildReport {
  id: string;
  status: 'to_complete' | 'draft' | 'submitted' | 'validated' | 'overdue';
}

/** Child session inside a {@link SessionGroup}. */
export interface SessionGroupChild {
  id: string;
  sessionGroupId: string;
  status: string;
  sessionDate: string;
  startTime: string;
  endTime: string;
  type: SessionType;
  establishmentId: string;
  companyId: string;
  participants?: SessionGroupParticipant[];
  studentTutorPairs?: SessionGroupStudentTutorPair[];
  sessionReport?: SessionGroupChildReport | null;
  // Per-session fields (present in the hydrated child, not on the group row).
  roomId?: string | null;
  programId?: string | null;
  schoolYearId?: string | null;
  classId?: string | null;
  specialtyIds?: string[] | null;
  subjectIds?: string[];
  levelIds?: string[];
  activityId?: string | null;
  sessionFormat?: SessionFormat;
  topic?: string | null;
  joinLink?: string | null;
  internalNotes?: string | null;
  durationMinutes?: number | null;
  planDuringHolidays?: boolean;
  mode?: string;
  recurrenceType?: string;
  recurrenceRule?: Record<string, unknown> | null;
  recurrenceGroupId?: string | null;
}

/** Parent session grouping several child sessions (collective planning). */
export interface SessionGroup {
  id: string;
  createdAt: string;
  updatedAt: string;
  companyId: string;
  establishmentId: string;
  schoolYearId: string;
  sessionDate: string;
  startTime: string;
  endTime: string;
  type: SessionType;
  sessionFormat: SessionFormat;
  subjectIds?: string[];
  levelIds?: string[];
  programId?: string | null;
  activityId?: string | null;
  establishmentName?: string | null;
  programName?: string | null;
  seriesId?: string | null;
  status: SessionGroupStatus;
  createdById?: string | null;
  sessions: SessionGroupChild[];
}

/** Paginated `GET /session-groups` payload. */
export interface SessionGroupsResponse {
  data: SessionGroup[];
  total: number;
  page: number;
  pageSize: number;
}

/** Patch / create body for one child inside a session group. */
export interface UpdateSessionGroupChildDto {
  /** Existing child session id; omit to create a new child. */
  sessionId?: string;
  tutorIds?: string[];
  studentIds?: string[];
  volunteerIds?: string[];
  pairs?: Array<{ studentUserId: string; tutorUserId: string }>;
}

/** Body for `PATCH /session-groups/:id`. */
export interface UpdateSessionGroupDto {
  establishmentId?: string;
  roomId?: string | null;
  programId?: string | null;
  schoolYearId?: string;
  levelIds?: string[];
  subjectIds?: string[];
  classId?: string | null;
  specialtyIds?: string[] | null;
  sessionDate?: string;
  startTime?: string;
  endTime?: string;
  type?: SessionType;
  sessionFormat?: SessionFormat;
  activityId?: string | null;
  joinLink?: string | null;
  topic?: string | null;
  durationMinutes?: number | null;
  internalNotes?: string | null;
  establishmentName?: string | null;
  programName?: string | null;
  activityName?: string | null;
  roomName?: string | null;
  planDuringHolidays?: boolean;
  /** Full child composition; when provided, children are reconciled. */
  sessions?: UpdateSessionGroupChildDto[];
  /**
   * Scope for a group belonging to a recurring series.
   * `occurrence` (default) updates only this parent session; `following` also propagates
   * shared metadata to this and all later parent sessions of the same series.
   */
  recurrenceUpdateScope?: RecurrenceUpdateScope;
}

/** Slim in-person session row for list views. */
export interface SessionListItem {
  id: string;
  sessionDate: Date;
  startTime: string;
  endTime: string;
  type: SessionType;
  status: SessionStatus;
  recurrenceType?:
    | 'once'
    | 'daily'
    | 'weekly'
    | 'biweekly'
    | 'monthly'
    | 'several_days_per_week'
    | 'custom'
    | 'none';
  establishmentName?: string | null;
  establishment?: {
    id: string;
    name: string;
  };
  subject?: {
    id: string;
    label: string;
  };
  level?: {
    id: string;
    label: string;
  };
  room?: {
    id: string;
    name: string;
  };
  activity?: {
    id: string;
    name: string;
    maxStudentsPerTutor?: number;
  };
  participants?: Array<{
    id: string;
    userId: string;
    role: 'tutor' | 'student' | 'volunteer';
    user?: {
      id: string;
      firstName: string;
      lastName: string;
      displayName?: string;
      email?: string;
      userProfiles?: Array<{
        id: string;
        subjects?: unknown;
        level?: { id: string; label: string };
      }>;
    };
  }>;
  studentTutorPairs?: Array<{
    id: string;
    sessionId: string;
    studentUserId: string;
    tutorUserId: string;
    assignmentSource: string;
  }>;
}

/** Paginated `GET /sessions` payload. */
export interface SessionsResponse {
  data: SessionListItem[];
  total: number;
  page: number;
  pageSize: number;
}

/** `PATCH /sessions/:sessionId/participants/:participantId` body. */
export interface UpdateParticipantAttendanceDto {
  attendanceStatus: AttendanceStatus;
  absenceJustification?: string | null;
  absenceComment?: string | null;
}

/** Overlap result from `GET /sessions/check-tutor-availability`. */
export interface TutorAvailabilityResult {
  available: boolean;
  conflictingSession?: {
    id: string;
    sessionDate: string;
    startTime: string;
    endTime: string;
  };
}

/** Same shape as {@link TutorAvailabilityResult} for students. */
export type StudentAvailabilityResult = TutorAvailabilityResult;

/** Result from `GET /sessions/check-room-capacity`. */
export interface ClassroomCapacityResult {
  available: boolean;
  roomName: string;
  capacity: number | null;
  currentParticipants: number;
  participantsToAdd: number;
  totalIfAdded: number;
  remaining: number | null;
  message?: string;
}

/** `POST /sessions/recurrence-preview` body. */
export interface RecurrencePreviewInput {
  schoolYearId?: string;
  establishmentId?: string | null;
  /** Session type so RI/RA use their monthly/yearly clamp. */
  type?: string;
  sessionDate?: string;
  recurrenceType?: string;
  recurrenceRule?: Record<string, unknown> | null;
  planDuringHolidays?: boolean;
  vacationZoneId?: string | null;
  tutorIds?: string[];
}

/** `POST /sessions/recurrence-preview` payload. */
export interface RecurrencePreview {
  /** Sessions a single item would create — multiply by the number of items sent. */
  count: number;
  dates: string[];
  schoolYearEndDate: string;
  /** min(tutor engagement end, school year end) */
  planningEndDate?: string;
}

/** Status of an async `POST /sessions/bulk-async` job. */
export type SessionBulkJobStatus = 'pending' | 'running' | 'completed' | 'failed';

/** `GET /sessions/bulk-jobs/:jobId` payload. */
export interface SessionBulkJob {
  id: string;
  status: SessionBulkJobStatus;
  /** Submitted items (one per tutor group), not the sessions they expand to. */
  totalItems: number;
  processedItems: number;
  failedItems: number;
  /** Sessions actually inserted so far, across every processed item. */
  createdSessions: number;
  /** Sessions the payload should produce, recurrence expanded. Null when not computable. */
  expectedSessions: number | null;
  result: {
    summary: { total: number; created: number; failed: number; sessions: number };
    results: Array<
      | { index: number; status: 'created'; sessionIds: string[] }
      | { index: number; status: 'error'; error: string }
    >;
  } | null;
  error: string | null;
}

/** A bulk create the server has accepted, before any session exists. */
export interface QueuedSessionsBulkJob {
  jobId: string;
  status: SessionBulkJobStatus;
  /** Submitted items (one per tutor group), not the sessions they expand to. */
  totalItems: number;
  /** Sessions the payload should produce, recurrence expanded. Null when not computable. */
  expectedSessions: number | null;
}

/** Callbacks for following a queued bulk session-create job. */
export interface SessionsBulkTrackOptions {
  /** Held rather than read from context: the tracker keeps running after its caller unmounts. */
  queryClient: import('@tanstack/react-query').QueryClient;
  /** One session per tutor group, which changes how the result reads. */
  isGroupMode: boolean;
  /**
   * Every poll, for a caller that stayed open and wants to show the same counts as the toast.
   * Safe to leave wired to component state: once unmounted the updates simply go nowhere.
   */
  onProgress?: (job: SessionBulkJob) => void;
  /**
   * The job has landed and the sessions exist. Separate from queuing on purpose — this is the point
   * where a caller can refresh what depends on them, which is not true when the job is accepted.
   */
  onSettled?: () => void;
}

/** Query params for `GET /session-groups`. */
export interface ListSessionGroupsParams {
  page?: number;
  pageSize?: number;
  dateFrom?: string;
  dateTo?: string;
  statuses?: SessionGroupStatus[];
  establishmentId?: string;
  types?: SessionType[];
  schoolYearId?: string;
  subjectId?: string;
  levelId?: string;
  tutorId?: string;
  animatorUserId?: string;
  search?: string;
  seriesId?: string;
  recurrenceType?: string;
  sortOrder?: 'asc' | 'desc';
}

/** Group mode: 1 session per tutor, 1 tutor per student (homework help, workshop, tutoring, quick help desk). */
export const GROUP_SESSION_TYPES = [
  'homework_help',
  'workshop',
  'group_tutoring',
  'quick_help_desk',
] as const satisfies readonly SessionType[];

/** One of {@link GROUP_SESSION_TYPES} (1 session per tutor). */
export type GroupSessionType = (typeof GROUP_SESSION_TYPES)[number];

/** Types allowed in collective planning. */
export const COLLECTIVE_SESSION_TYPE_VALUES: SessionType[] = [
  'homework_help',
  'workshop',
  'whole_class',
  'group_tutoring',
  'quick_help_desk',
];

/** True when `type` is a group session (1 tutor per student in that session). */
export function isGroupSessionType(
  type: SessionType | '' | null | undefined,
): type is GroupSessionType {
  return type != null && type !== '' && (GROUP_SESSION_TYPES as readonly string[]).includes(type);
}
